/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { MemoryRouter, Route, matchPath, useLocation } from 'react-router-dom';

import BiddingQuoteContractUpload from 'components/atoms/bidding_quote_contract_upload';
import BiddingQuoteIntro from 'components/atoms/bidding_quote_intro';
import BiddingQuoteStart from 'components/atoms/bidding_quote_start';
import ConlicitasegData from 'components/atoms/conlicitaseg_data';
import InsuranceSuccess from 'components/atoms/insurance_success';
import PaymentInfo from 'components/atoms/payment_info';
import ProcessInfo from 'components/atoms/process_info';
import QuoteConfimation from 'components/atoms/quote_confirmation';
import QuoteContractInfo from 'components/atoms/quote_contract_info';
import QuoteHolderData from 'components/atoms/quote_holder_data';
import QuotesSelection from 'components/atoms/quotes_selection';
import ReasonsNewInsurance from 'components/atoms/reasons_new_insurance';
import AiManagementDrawer from 'components/molecules/ai_management_drawer';
import { ConlicitasegContact } from 'pages/ConlicitasegContact';
import LegacyQuoteRedirects from 'routes/legacy_quote_redirects';
import { Container } from './styles';

export type ConlicitasegQuoteTipo = 'participacao' | 'execucao';

interface ConlicitasegQuotePanelProps {
  biddingId: number;
  tipo: ConlicitasegQuoteTipo;
  open: boolean;
  onClose: () => void;
  // Cotações devolvidas (/cotacoes/:processId) ou apólice emitida (/informacoes).
  onConcluido?: (processId: number | null) => void;
  // Saída do erro de carregamento: fechar e enviar a garantia como documento.
  onSendDocument?: () => void;
}

// Chama onConcluido quando a navegação interna chega ao fim do fluxo; o fluxo não tem evento
// próprio de conclusão.
const ConclusionWatcher: React.FC<{ onConcluido?: (processId: number | null) => void }> = ({
  onConcluido,
}) => {
  const { pathname } = useLocation();
  const avisados = useRef(new Set<string>());
  const onConcluidoRef = useRef(onConcluido);
  onConcluidoRef.current = onConcluido;

  useEffect(() => {
    const cotacoes = matchPath<{ processId: string }>(pathname, {
      path: '/conlicitaseg/cotacoes/:processId',
    });
    const emitida = matchPath(pathname, { path: '/conlicitaseg/informacoes' });
    if (!cotacoes && !emitida) return;
    const chave = cotacoes ? `cotacoes-${cotacoes.params.processId}` : 'informacoes';
    if (avisados.current.has(chave)) return;
    avisados.current.add(chave);
    onConcluidoRef.current?.(cotacoes ? Number(cotacoes.params.processId) || null : null);
  }, [pathname]);

  return null;
};

// A lista do ConLicitaSeg é para onde os passos mandam ao cancelar ou terminar: no painel, fecha.
const CloseOnList: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    onClose();
  }, [onClose]);
  return null;
};

interface BoundaryProps {
  onRetry: () => void;
  onSendDocument?: () => void;
}

class PanelErrorBoundary extends React.Component<BoundaryProps, { erro: boolean }> {
  constructor(props: BoundaryProps) {
    super(props);
    this.state = { erro: false };
  }

  static getDerivedStateFromError() {
    return { erro: true };
  }

  render() {
    const { erro } = this.state;
    const { children, onRetry, onSendDocument } = this.props;
    if (!erro) return children;
    return (
      <div className="alert alert-warning">
        <p className="mb-2">Não foi possível carregar o ConLicitaSeg.</p>
        <button
          type="button"
          className="btn btn-sm btn-primary mr-2"
          onClick={() => {
            this.setState({ erro: false });
            onRetry();
          }}
        >
          Tentar de novo
        </button>
        {onSendDocument && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={onSendDocument}
          >
            Enviar documento
          </button>
        )}
      </div>
    );
  }
}

// Fluxo de cotação do ConLicitaSeg num painel lateral, sem nova aba e sem mudar a URL da página:
// os mesmos passos da página cheia rodam num MemoryRouter próprio. Reutilizável fora da proposta.
const ConlicitasegQuotePanel: React.FC<ConlicitasegQuotePanelProps> = ({
  biddingId,
  tipo,
  open,
  onClose,
  onConcluido,
  onSendDocument,
}) => {
  // Fechar desmonta o fluxo; "Tentar de novo" o recomeça do início.
  const [tentativa, setTentativa] = useState(0);

  if (!open) return null;

  return (
    <AiManagementDrawer
      title="Cotação da Garantia"
      subtitle="ConLicitaSeg"
      width="1120px"
      bodyClassName="p-0 flex-grow-1"
      onClose={onClose}
    >
      <Container>
        <PanelErrorBoundary
          onRetry={() => setTentativa((n) => n + 1)}
          onSendDocument={onSendDocument}
        >
          <MemoryRouter
            key={tentativa}
            initialEntries={[`/conlicitaseg/garantia/nova?bidding_id=${biddingId}&tipo=${tipo}`]}
          >
            <ConclusionWatcher onConcluido={onConcluido} />
            <Route path="/conlicitaseg" exact render={() => <CloseOnList onClose={onClose} />} />
            <Route path="/conlicitaseg/processo/:processId/:quoteId" component={ProcessInfo} />
            <Route path="/conlicitaseg/novo" component={BiddingQuoteIntro} />
            <Route path="/conlicitaseg/garantia/motivo" component={ReasonsNewInsurance} />
            <Route path="/conlicitaseg/garantia/dados" component={ConlicitasegData} />
            <Route path="/conlicitaseg/garantia/nova" component={BiddingQuoteStart} />
            <Route
              path="/conlicitaseg/garantia/upload-contrato"
              component={BiddingQuoteContractUpload}
            />
            <Route path="/conlicitaseg/garantia/tomador-segurado" component={QuoteHolderData} />
            <Route path="/conlicitaseg/garantia/licitacao-contrato" component={QuoteContractInfo} />
            <LegacyQuoteRedirects />
            <Route path="/conlicitaseg/garantia/confirmacao" component={QuoteConfimation} />
            <Route path="/conlicitaseg/cotacoes/:processId" component={QuotesSelection} />
            <Route path="/conlicitaseg/pagamento" component={PaymentInfo} />
            <Route path="/conlicitaseg/informacoes" component={InsuranceSuccess} />
            <Route path="/conlicitaseg/contato" component={ConlicitasegContact} />
          </MemoryRouter>
        </PanelErrorBoundary>
      </Container>
    </AiManagementDrawer>
  );
};

export default ConlicitasegQuotePanel;
